import { useLayoutEffect, useMemo, useRef } from 'react';
import { cn } from '../../../lib/utils/cn';
import { getTimeOptionId, getTimeOptions, getNearestTimeOption, type TimeOption } from './utils';

type TimeSuggestionsPopoverProps = {
  activeValue: string;
  onSelect: (value: string) => void;
  step?: number;
  defaultOpenValue?: string;
  is24Hour?: boolean;
};

const OPTION_HEIGHT = 45; // px per option row
const VISIBLE_ROWS = 7; // visible rows in the list

export const TimeSuggestionsPopover = ({
  activeValue,
  onSelect,
  step = 15,
  defaultOpenValue = '08:00',
  is24Hour,
}: TimeSuggestionsPopoverProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const skipScrollRef = useRef(false);
  const options = useMemo(() => getTimeOptions(step, is24Hour), [step, is24Hour]);

  const handleSelect = (val: string) => {
    skipScrollRef.current = true;
    onSelect(val);
  };

  // Auto-scroll to nearest option on mount — deferred to after paint so portal DOM is laid out
  useLayoutEffect(() => {
    if (skipScrollRef.current) {
      skipScrollRef.current = false;
      return;
    }

    const list = listRef.current;
    if (!list) return;

    const nearest = getNearestTimeOption(activeValue || defaultOpenValue, options);
    if (!nearest) return;

    const index = options.findIndex((o) => o.value === nearest.value);
    if (index < 0) return;

    // Center the nearest option in the visible area
    const targetScroll = index * OPTION_HEIGHT - Math.floor(VISIBLE_ROWS / 2) * OPTION_HEIGHT;
    list.scrollTop = Math.max(0, targetScroll);
  }, [activeValue, defaultOpenValue, options]);

  return (
    <div
      ref={listRef}
      className="overflow-y-auto overscroll-contain px-2 scrollbar-thin"
      style={{ maxHeight: OPTION_HEIGHT * VISIBLE_ROWS }}>
      {options.map((option) => (
        <TimeOptionRow
          key={option.value}
          option={option}
          isActive={option.value === activeValue}
          onSelect={handleSelect}
        />
      ))}
    </div>
  );
};

const TimeOptionRow = ({
  option,
  isActive,
  onSelect,
}: {
  option: TimeOption;
  isActive: boolean;
  onSelect: (value: string) => void;
}) => (
  <button
    type="button"
    id={getTimeOptionId(option.value)}
    tabIndex={-1}
    className={cn(
      'w-full rounded-lg px-4 py-3 text-left typo-base',
      isActive ? 'bg-indigo-crush-10' : 'hover:bg-space-04',
    )}
    style={{ height: OPTION_HEIGHT }}
    onMouseDown={(event) => event.preventDefault()}
    onClick={() => onSelect(option.value)}>
    {option.label}
  </button>
);
